// frontend/src/components/admin/BillingConfigManager.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Loading from '../common/Loading';

const BillingConfigManager = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/meter-indexes/config');
      setConfig(response.data.data);
      setFormData(response.data.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/meter-indexes/config', formData);
      alert('Configuration mise à jour avec succès !');
      fetchConfig();
    } catch (error) {
      alert('Erreur: ' + error.response?.data?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (section, field, value) => {
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: parseFloat(value) || 0,
      },
    });
  };

  const handleTimeChange = (index, value) => {
    const newTimes = [...formData.captureSchedule.times];
    newTimes[index] = value;
    setFormData({
      ...formData,
      captureSchedule: {
        ...formData.captureSchedule,
        times: newTimes,
      },
    });
  };

  const handleAddTime = () => {
    setFormData({
      ...formData,
      captureSchedule: {
        ...formData.captureSchedule,
        times: [...formData.captureSchedule.times, '12:00'],
      },
    });
  };

  const handleRemoveTime = (index) => {
    const newTimes = formData.captureSchedule.times.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      captureSchedule: {
        ...formData.captureSchedule,
        times: newTimes,
      },
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      {/* Header avec bouton retour */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 hover:text-blue-800 font-semibold flex items-center"
          >
            ← Retour au Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            ⚙️ Configuration de facturation
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Paramètres généraux */}
        <Card title="💰 Paramètres généraux">
          <div className="space-y-4">
            <Input
              label="Prix unitaire HT (FCFA/kWh)"
              type="number"
              step="0.01"
              value={formData?.general?.pricePerKwhHT}
              onChange={(e) => handleChange('general', 'pricePerKwhHT', e.target.value)}
            />
            <Input
              label="Taux de TVA (%)"
              type="number"
              step="0.01"
              value={(formData?.general?.tvaRate * 100).toFixed(2)}
              onChange={(e) => handleChange('general', 'tvaRate', e.target.value / 100)}
            />
            <Input
              label="Redevance électrification rurale (FCFA - bimestrielle)"
              type="number"
              value={formData?.general?.redevanceElectrificationRurale}
              onChange={(e) =>
                handleChange('general', 'redevanceElectrificationRurale', e.target.value)
              }
            />
            <Input
              label="Taxe ordures ménagères (FCFA)"
              type="number"
              value={formData?.general?.taxeOrduresMenageres}
              onChange={(e) => handleChange('general', 'taxeOrduresMenageres', e.target.value)}
            />
            <Input
              label="Redevance RTI (FCFA)"
              type="number"
              value={formData?.general?.redevanceRTI}
              onChange={(e) => handleChange('general', 'redevanceRTI', e.target.value)}
            />
            <Input
              label="Timbre d'État (FCFA)"
              type="number"
              value={formData?.general?.timbreEtat}
              onChange={(e) => handleChange('general', 'timbreEtat', e.target.value)}
            />
          </div>
        </Card>

        {/* Horaires de capture */}
        <Card title="🕐 Horaires de capture automatique">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData?.captureSchedule?.enabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    captureSchedule: {
                      ...formData.captureSchedule,
                      enabled: e.target.checked,
                    },
                  })
                }
                className="w-5 h-5 text-blue-600 rounded"
              />
              <label className="text-gray-700 font-medium">
                Activer les captures automatiques
              </label>
            </div>

            <div>
              <p className="text-sm text-gray-700 font-medium mb-3">
                Horaires configurés:
              </p>
              <div className="space-y-2">
                {formData?.captureSchedule?.times.map((time, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => handleTimeChange(index, e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => handleRemoveTime(index)}
                      className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                      disabled={formData.captureSchedule.times.length <= 1}
                    >
                      🗑️ Supprimer
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={handleAddTime}
                className="mt-3 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                ➕ Ajouter un horaire
              </button>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Les captures automatiques s'effectuent tous les jours
                aux horaires définis. Le 1er de chaque mois à 00:01, un index de référence est
                automatiquement créé.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Boutons d'action */}
      <div className="mt-6 flex justify-end space-x-3">
        <Button variant="secondary" onClick={() => navigate('/dashboard')}>
          Annuler
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Sauvegarde...' : '💾 Sauvegarder la configuration'}
        </Button>
      </div>
    </div>
    
  );
};

export default BillingConfigManager;